import { useState, useContext, useEffect } from 'react';
import { Box, Button, Stack } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewIcon from '@mui/icons-material/GridView';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ThemeContext, LeftPanelContext } from '../Context';
import Elements from './Elements';
import Rubrics from './Rubrics';
import StandardReports from './StandardReports';
import Appendix from './Appendix';

const TABS = [
  { id: 'elements', label: 'Encodage', Icon: EditNoteIcon },
  { id: 'rubrics', label: 'Rubriques', Icon: FormatListBulletedIcon },
  { id: 'standardReports', label: 'Canevas', Icon: GridViewIcon },
  { id: 'appendix', label: 'Annexes', Icon: AttachFileIcon }
];

// Partie gauche (left) de l'application, elle propose la liste des rubriques à modifier.
function Left() {
  const { activeView, setActiveView, highlightTarget } = useContext(LeftPanelContext);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const [flashedTab, setFlashedTab] = useState(null);
  const { theme } = useContext(ThemeContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Flash temporaire de l'onglet ciblé par le guide de démarrage
  useEffect(() => {
    if (!highlightTarget) return;
    setFlashedTab(highlightTarget.view);
    const timer = setTimeout(() => setFlashedTab(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightTarget]);

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Barre de boutons fixée en haut */}
      <Box
        sx={{
          flexShrink: 0, // Empêche la barre de boutons de rétrécir
          zIndex: 10
        }}
      >
        <Stack 
          direction="row" 
          spacing={0} 
          sx={{ 
            background: isDark 
              ? 'rgb(40, 57, 73)' 
              : 'rgb(218, 230, 236)',
            boxShadow: isDark 
              ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
              : '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            containerType: 'inline-size',
          }}
        >
          {TABS.map(({ id, label, Icon }) => {
            const isActive = activeView === id;
            const isFlashed = flashedTab === id;
            return (
              <Button
                key={id}
                onClick={() => handleViewChange(id)}
                startIcon={
                  <Icon
                    className="tab-icon"
                    sx={{ fontSize: '16px !important' }}
                  />
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: isActive ? 700 : 500,
                  py: 1,
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  color: isActive
                    ? 'primary.main'
                    : (isDark ? '#bdc3c7' : '#666'),
                  backgroundColor: isActive
                    ? (isDark ? '#1a252f' : '#f5f5f5')
                    : 'transparent',
                  borderRadius: 0,
                  position: 'relative',
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0
                  },
                  // Masquer les icônes quand la colonne devient trop étroite
                  '@container (max-width: 300px)': {
                    '& .MuiButton-startIcon': {
                      display: 'none'
                    }
                  },
                  '&:hover': {
                    backgroundColor: isActive
                      ? (isDark ? '#1a252f' : '#f5f5f5')
                      : (isDark ? 'rgba(79, 195, 247, 0.1)' : 'rgba(255, 255, 255, 0.6)'),
                    color: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                  animation: isFlashed ? 'tabFlash 1s ease-in-out 3' : 'none',
                  '@keyframes tabFlash': {
                    '0%, 100%': {
                      backgroundColor: isActive
                        ? (isDark ? '#1a252f' : '#f5f5f5')
                        : 'transparent'
                    },
                    '50%': {
                      backgroundColor: isDark
                        ? 'rgba(79, 195, 247, 0.35)'
                        : 'rgba(25, 118, 210, 0.25)'
                    }
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '3px',
                    backgroundColor: 'primary.main',
                    transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                    transition: 'transform 0.25s ease',
                  },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Stack>
      </Box>

      {/* Zone de contenu défilable */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          backgroundColor: isDark ? '#1a252f' : 'inherit'
        }}
      >
        {/* Tous les composants sont toujours montés pour préserver les états, mais masqués visuellement */}
        <Box sx={{ display: activeView === 'elements' ? 'block' : 'none', height: '100%' }}>
          <Elements />
        </Box>
        <Box sx={{ display: activeView === 'rubrics' ? 'block' : 'none', height: '100%' }}>
          <Rubrics />
        </Box>
        <Box sx={{ display: activeView === 'standardReports' ? 'block' : 'none', height: '100%' }}>
          <StandardReports />
        </Box>
        <Box sx={{ display: activeView === 'appendix' ? 'block' : 'none', height: '100%' }}>
          <Appendix />
        </Box>
      </Box>
    </Box>
  );
}

export default Left;



